import React from 'react'
import { ChevronRight, SlidersHorizontal } from 'lucide-react'
import { cn } from './cn'

export interface SystemSettingsPathProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'children' | 'role' | 'aria-label'
> {
  /** The levels to go through, outermost first; the last is the thing to find. */
  segments: readonly string[]
  /** The whole path as one sentence, for assistive tech (i18n at the call site). */
  label: string
}

/**
 * 系統設定路徑（design-guideline §7.8，設計側 `.syspath`）：告訴使用者去作業系統設定的哪裡開。
 * 說明文字講「這是做什麼的」，路徑講「在哪裡」，所以是細框小標籤而不是一行說明：開頭一個
 * 設定圖示，每一層用小箭頭隔開，最後一層墨色加粗，不用底色。元件本身不帶 margin。
 */
export function SystemSettingsPath({
  segments,
  label,
  className,
  ...rest
}: SystemSettingsPathProps) {
  const last = segments.length - 1
  return (
    <span
      {...rest}
      role="note"
      aria-label={label}
      data-ui="system-settings-path"
      className={cn(
        'inline-flex flex-wrap items-center gap-x-1 gap-y-[3px] rounded-[var(--radius-sm)] border border-solid border-[var(--line)] py-[3px] pr-2 pl-1.5 text-2xs leading-[1.4] text-muted',
        className
      )}
    >
      <SlidersHorizontal aria-hidden="true" className="mr-0.5 size-[13px] flex-none" />
      {segments.map((segment, index) => (
        <React.Fragment key={`${index}:${segment}`}>
          {index > 0 ? (
            <ChevronRight aria-hidden="true" strokeWidth={2} className="size-2.5 flex-none" />
          ) : null}
          {index === last ? (
            <b data-path-segment="" className="font-semibold text-foreground">
              {segment}
            </b>
          ) : (
            <span data-path-segment="">{segment}</span>
          )}
        </React.Fragment>
      ))}
    </span>
  )
}
